import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Clock, Mail, LogOut, Calendar, FileText, Image, Video, Edit3, Trash2, HardDrive, AlertCircle, Inbox, Send, Shield, Download, Eye, EyeOff, CreditCard, Lock, XCircle, Bookmark, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { API } from "@/App";
import { format } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import { toast } from "sonner";
import { BizouLogo, KissIcon } from "@/components/BizouLogo";
import { reopenCookieBanner } from "@/components/CookieBanner";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useTranslation } from "@/i18n/LanguageContext";

export default function Dashboard({ user, logout, setUser }) {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const dateLocale = lang === "fr" ? frLocale : undefined;
  const [sentMessages, setSentMessages] = useState([]);
  const [receivedMessages, setReceivedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [deleteAccountConfirm, setDeleteAccountConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState("sent");

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sentRes, recvRes] = await Promise.all([
        fetch(`${API}/messages`, { credentials: "include" }),
        fetch(`${API}/messages/received`, { credentials: "include" }),
      ]);
      if (sentRes.ok) setSentMessages(await sentRes.json());
      if (recvRes.ok) setReceivedMessages(await recvRes.json());
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error(t("toast.errorLoading"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMessage = async () => {
    if (!deleteConfirm) return;
    try {
      const response = await fetch(`${API}/messages/${deleteConfirm.message_id}`, { method: "DELETE", credentials: "include" });
      if (!response.ok) { const e = await response.json(); throw new Error(e.detail); }
      toast.success(t("toast.messageDeleted"));
      setDeleteConfirm(null);
      fetchAll();
    } catch (error) {
      toast.error(error.message || t("toast.deleteError"));
    }
  };

  const handleCancelMessage = async () => {
    if (!cancelConfirm) return;
    try {
      const response = await fetch(`${API}/messages/${cancelConfirm.message_id}/cancel`, { method: "POST", credentials: "include" });
      if (!response.ok) { const e = await response.json(); throw new Error(e.detail); }
      toast.success(t("dashboard.cancelledMessage"));
      setCancelConfirm(null);
      fetchAll();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const response = await fetch(`${API}/account`, { method: "DELETE", credentials: "include" });
      if (!response.ok) throw new Error();
      toast.success(t("toast.accountDeleted"));
      setDeleteAccountConfirm(false);
      if (setUser) setUser(null);
      navigate("/");
    } catch {
      toast.error(t("toast.deleteAccountError"));
    }
  };

  const handleExportData = async () => {
    try {
      const res = await fetch(`${API}/user/export`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bizou-export-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("toast.exportDownloaded"));
    } catch {
      toast.error(t("toast.exportError"));
    }
  };

  const getStatusBadge = (status) => {
    const clsMap = {
      pending_payment: "bg-amber-100 text-amber-800",
      pending_upgrade: "bg-orange-100 text-orange-800",
      paid: "bg-blue-100 text-blue-800",
      scheduled: "bg-emerald-100 text-emerald-800",
      draft: "bg-gray-100 text-gray-600",
      cancelled: "bg-red-100 text-red-700",
      reserved: "bg-violet-100 text-violet-800",
      sent: "bg-green-100 text-green-800",
    };
    const iconMap = {
      scheduled: <Lock className="w-3 h-3 mr-1 inline" />,
      cancelled: <XCircle className="w-3 h-3 mr-1 inline" />,
      reserved: <Bookmark className="w-3 h-3 mr-1 inline" />,
    };
    const cls = clsMap[status] || "bg-gray-100 text-gray-800";
    return (
      <span className={`badge ${cls} flex items-center`} data-testid={`status-${status}`}>
        {iconMap[status] || null}
        {t(`dashboard.status.${status}`) || status}
      </span>
    );
  };

  const renderGiftedBadge = (msg) => {
    if (msg.payment_status !== "gifted") return null;
    return (
      <span
        className="badge bg-amber-100 text-amber-800 flex items-center ml-1"
        data-testid={`gifted-badge-${msg.message_id}`}
        title={t("dashboard.giftedBadgeTitle") || "Bizou offert (interne, sans paiement Stripe)"}
      >
        <span className="mr-1">🎁</span>
        {t("dashboard.giftedBadge") || "Cadeau"}
      </span>
    );
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "video": return <Video className="w-5 h-5 text-[#E8175D]" />;
      case "photo": return <Image className="w-5 h-5 text-[#E8175D]" />;
      default: return <FileText className="w-5 h-5 text-[#E8175D]" />;
    }
  };

  const getFilesCount = (msg) => (msg.files?.length > 0 ? msg.files.length : msg.file_name ? 1 : 0);

  const renderSentMessage = (msg) => {
    const filesCount = getFilesCount(msg);
    return (
      <div key={msg.message_id} className="message-card flex flex-col gap-4 p-6" data-testid={`sent-card-${msg.message_id}`}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-[#E8175D]/5 flex items-center justify-center flex-shrink-0">{getTypeIcon(msg.message_type)}</div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-[#1C1917] truncate">{msg.title || t("dashboard.noTitle")}</h3>
            <p className="text-sm text-[#78716C]">{t("dashboard.for")} {msg.recipient_name} ({msg.recipient_email})</p>
          </div>
          {getStatusBadge(msg.status)}
          {renderGiftedBadge(msg)}
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm text-[#78716C]">
          <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /><span>{msg.delivery_date ? `${t("dashboard.deliveryLabel")} ${format(new Date(msg.delivery_date), "d MMM yyyy", { locale: dateLocale })}` : t("dashboard.deliveryLabel") + " —"}</span></div>
          {filesCount > 0 && <div className="flex items-center gap-2"><HardDrive className="w-4 h-4" /><span>{filesCount} {filesCount > 1 ? t("dashboard.files") : t("dashboard.file")}</span></div>}
        </div>
        <div className="bg-[#FDF0F4] rounded-xl p-4"><p className="text-sm text-[#1C1917] line-clamp-2">{msg.content}</p></div>
        {(msg.status === "scheduled" || msg.status === "paid") && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-800" data-testid={`sealed-banner-${msg.message_id}`}>
              <Lock className="w-4 h-4 flex-shrink-0" />
              <span>{t("dashboard.sealedMessage")}</span>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-[#F0E8EB]">
              <Button variant="outline" size="sm" onClick={() => setCancelConfirm(msg)} className="text-amber-600 hover:text-amber-700 hover:border-amber-300" data-testid={`cancel-btn-${msg.message_id}`}>
                <XCircle className="w-4 h-4 mr-2" />{t("dashboard.cancelMessage")}
              </Button>
            </div>
          </div>
        )}
        {msg.status === "cancelled" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700" data-testid={`cancelled-banner-${msg.message_id}`}>
              <XCircle className="w-4 h-4 flex-shrink-0" />
              <span>{t("dashboard.cancelledMessage")}</span>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-[#F0E8EB]">
              <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(msg)} className="text-red-500 hover:text-red-600 hover:border-red-300" data-testid={`delete-btn-${msg.message_id}`}><Trash2 className="w-4 h-4 mr-2" />{t("common.delete")}</Button>
            </div>
          </div>
        )}
        {msg.status === "reserved" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-4 py-3 bg-violet-50 border border-violet-200 rounded-xl text-sm text-violet-800" data-testid={`reserved-banner-${msg.message_id}`}>
              <Bookmark className="w-4 h-4 flex-shrink-0" />
              <span>{t("dashboard.reservedMessage")}</span>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-[#F0E8EB]">
              <Button size="sm" onClick={() => navigate(`/create?edit=${msg.message_id}`)} className="bg-[#E8175D] text-white hover:bg-[#C4134F]" data-testid={`compose-btn-${msg.message_id}`}>
                <PenLine className="w-4 h-4 mr-2" />{t("dashboard.composeReserved")}
              </Button>
            </div>
          </div>
        )}
        {msg.status !== "sent" && msg.status !== "scheduled" && msg.status !== "paid" && msg.status !== "cancelled" && (
          <div className="flex items-center gap-3 pt-2 border-t border-[#F0E8EB]">
            {(msg.status === "pending_payment" || msg.status === "pending_upgrade") && (
              <Button size="sm" onClick={() => navigate(`/create?edit=${msg.message_id}`)} className="bg-[#E8175D] text-white hover:bg-[#C4134F]" data-testid={`pay-btn-${msg.message_id}`}>
                <CreditCard className="w-4 h-4 mr-2" />
                {msg.status === "pending_upgrade" ? t("dashboard.payUpgrade", { amount: msg.upgrade_diff }) : t("dashboard.payNow")}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate(`/create?edit=${msg.message_id}`)} className="text-[#78716C] hover:text-[#1C1917]" data-testid={`edit-btn-${msg.message_id}`}><Edit3 className="w-4 h-4 mr-2" />{t("dashboard.editMessage")}</Button>
            <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(msg)} className="text-red-500 hover:text-red-600 hover:border-red-300" data-testid={`delete-btn-${msg.message_id}`}><Trash2 className="w-4 h-4 mr-2" />{t("common.delete")}</Button>
          </div>
        )}
        {msg.status === "sent" && (
          <div className="flex items-center gap-3 text-sm pt-2 border-t border-[#F0E8EB]">
            <div className="flex items-center gap-2 text-[#84A98C]">
              <KissIcon className="w-4 h-4 text-[#84A98C]" /><span>{t("dashboard.sentOn")} {format(new Date(msg.sent_at), "d MMM yyyy", { locale: dateLocale })}</span>
            </div>
            <span className="mx-1">•</span>
            {msg.opened ? (
              <span className="flex items-center gap-1 text-[#E8175D] font-medium" data-testid={`opened-badge-${msg.message_id}`}>
                <Eye className="w-3.5 h-3.5" />{t("dashboard.opened")}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[#A8A29E]" data-testid={`not-opened-badge-${msg.message_id}`}>
                <EyeOff className="w-3.5 h-3.5" />{t("dashboard.notOpened")}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderReceivedMessage = (msg) => {
    const filesCount = getFilesCount(msg);
    return (
      <div
        key={msg.message_id}
        className="message-card flex flex-col gap-4 p-6 cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => navigate(`/message/${msg.read_token}`)}
        data-testid={`received-card-${msg.message_id}`}
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-[#E8175D]/10 flex items-center justify-center flex-shrink-0">
            <KissIcon className="w-6 h-6 text-[#E8175D]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-[#1C1917] truncate">{msg.title || t("dashboard.receivedBizou")}</h3>
            <p className="text-sm text-[#78716C]">{t("dashboard.from")} {msg.sender_name}</p>
          </div>
          {getTypeIcon(msg.message_type)}
        </div>
        <div className="bg-[#FDF0F4] rounded-xl p-4"><p className="text-sm text-[#1C1917] line-clamp-2">{msg.content}</p></div>
        <div className="flex items-center gap-2 text-sm text-[#78716C]">
          <Calendar className="w-4 h-4" />
          <span>{t("dashboard.receivedOn")} {msg.sent_at ? format(new Date(msg.sent_at), "d MMM yyyy", { locale: dateLocale }) : "—"}</span>
          {filesCount > 0 && <><span className="mx-2">•</span><HardDrive className="w-4 h-4" /><span>{filesCount} {filesCount > 1 ? t("dashboard.files") : t("dashboard.file")}</span></>}
        </div>
      </div>
    );
  };

  const totalSent = sentMessages.length;
  const totalReceived = receivedMessages.length;

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white border-b border-[#F0E8EB]">
        <div className="container-bizou flex items-center justify-between h-16">
          <a href="/" className="flex items-center" data-testid="logo"><BizouLogo size="md" /></a>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <span className="hidden md:block text-sm text-[#78716C]">{user?.name}</span>
            <Button variant="ghost" onClick={() => { logout(); navigate("/"); }} className="btn-ghost" data-testid="logout-btn">
              <LogOut className="w-5 h-5" /><span className="hidden md:inline ml-2">{t("common.logout")}</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container-bizou py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-medium text-[#1C1917] mb-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="dashboard-title">
              {t("dashboard.greeting", { name: user?.name?.split(" ")[0] })}
            </h1>
            <p className="text-[#78716C]">{t("dashboard.subtitle")}</p>
          </div>
          <Button onClick={() => navigate("/create")} className="btn-primary" data-testid="create-message-btn">
            <Plus className="w-5 h-5 mr-2" />{t("dashboard.createNew")}
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner"></div></div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-[#FDF0F4] rounded-xl p-1 mb-8 h-12">
              <TabsTrigger value="sent" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm h-10 gap-2" data-testid="tab-sent">
                <Send className="w-4 h-4" />{t("dashboard.tabSent")} ({totalSent})
              </TabsTrigger>
              <TabsTrigger value="received" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm h-10 gap-2" data-testid="tab-received">
                <Inbox className="w-4 h-4" />{t("dashboard.tabReceived")} ({totalReceived})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="sent">
              {totalSent === 0 ? (
                <div className="card-bizou text-center py-12" data-testid="empty-sent">
                  <Mail className="w-16 h-16 text-[#E8175D] mx-auto mb-6" strokeWidth={1} />
                  <h3 className="text-xl font-medium text-[#1C1917] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{t("dashboard.emptySentTitle")}</h3>
                  <p className="text-[#78716C] mb-6">{t("dashboard.emptySentDesc")}</p>
                  <Button onClick={() => navigate("/create")} className="btn-primary"><KissIcon className="w-5 h-5 mr-2 kiss-pulse" />{t("dashboard.createFirst")}</Button>
                </div>
              ) : (
                <div className="space-y-4" data-testid="sent-list">{sentMessages.map(renderSentMessage)}</div>
              )}
            </TabsContent>

            <TabsContent value="received">
              {totalReceived === 0 ? (
                <div className="card-bizou text-center py-12" data-testid="empty-received">
                  <Inbox className="w-16 h-16 text-[#E8175D] mx-auto mb-6" strokeWidth={1} />
                  <h3 className="text-xl font-medium text-[#1C1917] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{t("dashboard.emptyReceivedTitle")}</h3>
                  <p className="text-[#78716C]">{t("dashboard.emptyReceivedSubDesc")}</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="received-list">{receivedMessages.map(renderReceivedMessage)}</div>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* RGPD Section */}
        <div className="mt-16 pt-8 border-t border-[#F0E8EB]" data-testid="rgpd-section">
          <h2 className="text-lg font-medium text-[#1C1917] mb-4 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            <Shield className="w-5 h-5 text-[#78716C]" />{t("dashboard.personalData")}
          </h2>
          <div className="flex flex-wrap gap-3">
            <a href="/privacy" className="text-sm text-[#78716C] hover:text-[#E8175D] underline">{t("dashboard.privacyPolicy")}</a>
            <span className="text-[#E7E5E4]">|</span>
            <a href="/invoices" className="text-sm text-[#78716C] hover:text-[#E8175D] underline flex items-center gap-1" data-testid="my-invoices-link"><FileText className="w-3.5 h-3.5" />{t("dashboard.myInvoices")}</a>
            <span className="text-[#E7E5E4]">|</span>
            <button onClick={reopenCookieBanner} className="text-sm text-[#78716C] hover:text-[#E8175D] underline" data-testid="manage-cookies-btn">{t("dashboard.manageCookies")}</button>
            <span className="text-[#E7E5E4]">|</span>
            <button onClick={handleExportData} className="text-sm text-[#78716C] hover:text-[#E8175D] underline flex items-center gap-1" data-testid="export-data-btn"><Download className="w-3.5 h-3.5" />{t("dashboard.exportData")}</button>
            <span className="text-[#E7E5E4]">|</span>
            <button onClick={() => setDeleteAccountConfirm(true)} className="text-sm text-red-500 hover:text-red-600 underline" data-testid="delete-account-btn">{t("dashboard.deleteAccount")}</button>
          </div>
        </div>
      </main>

      {/* Delete message dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertCircle className="w-5 h-5 text-red-500" />{t("dashboard.deleteMessageTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("dashboard.deleteMessageDesc", { title: deleteConfirm?.title || t("common.noTitle"), name: deleteConfirm?.recipient_name })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteMessage} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-btn">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel message dialog */}
      <AlertDialog open={!!cancelConfirm} onOpenChange={(open) => !open && setCancelConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><XCircle className="w-5 h-5 text-amber-500" />{t("dashboard.cancelConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("dashboard.cancelConfirmDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.back")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelMessage} className="bg-amber-500 hover:bg-amber-600" data-testid="confirm-cancel-btn">{t("dashboard.cancelConfirmBtn")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete account dialog */}
      <AlertDialog open={deleteAccountConfirm} onOpenChange={setDeleteAccountConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600"><AlertCircle className="w-5 h-5" />{t("dashboard.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("dashboard.deleteAccountFullDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAccount} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-account-btn">{t("dashboard.deletePermanently")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
